import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Circle, Users, MessageSquare, BookOpen, Zap, Settings, ExternalLink } from 'lucide-react';

const statuses = ['active', 'inactive', 'archived'];
const plans = ['starter', 'pro', 'enterprise'];

const SectionCard = ({ title, children, icon: Icon }) => (
  <div className="bg-[#121214] border border-white/10 rounded-xl">
    <div className="flex items-center gap-3 p-5 border-b border-white/5">
      <div className="w-8 h-8 rounded-lg bg-[#EF694C]/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#EF694C]" />
      </div>
      <h3 className="font-heading font-semibold text-white text-sm">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const chStatusColor = { active: 'text-[#10B981]', not_configured: 'text-[#52525b]', error: 'text-[#F43F5E]' };
const chLabels = { email: 'E-Mail', whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook', widget: 'Widget' };

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/superadmin/tenants/${id}`),
      api.get(`/tenants/${id}/users`).catch(() => ({ data: [] }))
    ]).then(([tr, ur]) => {
      setTenant(tr.data);
      setForm({ name: tr.data.name, status: tr.data.status, plan: tr.data.plan, chatwoot_config: tr.data.chatwoot_config || {}, ai_config: tr.data.ai_config || {}, branding: tr.data.branding || {} });
      setUsers(ur.data || []);
    }).catch(() => toast.error('Failed to load tenant'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/superadmin/tenants/${id}`, {
        name: form.name, status: form.status, plan: form.plan,
        chatwoot_config: form.chatwoot_config, ai_config: form.ai_config, branding: form.branding
      });
      setTenant(updated.data);
      toast.success('Mandant aktualisiert');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const setNested = (key, subKey, value) => {
    setForm(p => ({ ...p, [key]: { ...p[key], [subKey]: value } }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-8 h-8 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
    </div>
  );

  if (!tenant) return <div className="p-8 text-[#a1a1aa]">Mandant nicht gefunden</div>;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/superadmin/tenants')} data-testid="back-btn"
            className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-[#EF694C]/10 flex items-center justify-center">
            <span className="font-bold text-[#EF694C]">{tenant.name.charAt(0)}</span>
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-white">{tenant.name}</h2>
            <p className="text-xs text-[#52525b] font-mono">{tenant.slug}</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-tenant-btn"
          className="flex items-center gap-2 h-10 px-5 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold text-sm rounded-lg shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all active:scale-95 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Basic Info */}
          <SectionCard title="Basic Configuration" icon={Settings}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#a1a1aa] mb-1.5">Company Name</label>
                <input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  data-testid="tenant-name-input"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
              </div>
              <div>
                <label className="block text-xs text-[#a1a1aa] mb-1.5">Status</label>
                <select value={form.status || ''} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                  data-testid="tenant-status-select"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all">
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#a1a1aa] mb-1.5">Plan</label>
                <select value={form.plan || ''} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                  data-testid="tenant-plan-select"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all">
                  {plans.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </SectionCard>

          {/* Inbox Engine Config */}
          <SectionCard title="Inbox-Engine-Integration" icon={MessageSquare}>
            <div className="p-3 bg-[#3B82F6]/5 border border-[#3B82F6]/10 rounded-lg mb-4">
              <p className="text-xs text-[#a1a1aa]">
                <span className="text-[#3B82F6] font-medium">External dependency.</span> Deploy the Inbox Engine on your infrastructure and connect via API.{' '}
                <a href="https://www.chatwoot.com/docs/self-hosted" target="_blank" rel="noreferrer" className="text-[#EF694C] hover:underline inline-flex items-center gap-1">
                  Self-Hosted Docs <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Instance URL', key: 'url', placeholder: 'https://chatwoot.yourdomain.com' },
                { label: 'API Token', key: 'api_token', placeholder: 'woot_...' },
                { label: 'Account ID', key: 'account_id', placeholder: '1' },
                { label: 'Webhook Token', key: 'webhook_token', placeholder: 'auto-generated' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-[#a1a1aa] mb-1.5">{f.label}</label>
                  <input value={form.chatwoot_config?.[f.key] || ''} onChange={e => setNested('chatwoot_config', f.key, e.target.value)}
                    placeholder={f.placeholder} data-testid={`chatwoot-${f.key}`}
                    className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm font-mono placeholder-[#52525b] focus:outline-none focus:border-[#EF694C] transition-all" />
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Circle className={`w-2 h-2 fill-current ${form.chatwoot_config?.url ? 'text-[#F59E0B]' : 'text-[#52525b]'}`} />
              <span className="text-xs text-[#a1a1aa]">{form.chatwoot_config?.url ? 'Credentials pending verification' : 'Not configured'}</span>
            </div>
          </SectionCard>

          {/* AI Config */}
          <SectionCard title="AI Configuration" icon={Zap}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#a1a1aa] mb-1.5">Tone</label>
                <select value={form.ai_config?.tone || 'professional'} onChange={e => setNested('ai_config', 'tone', e.target.value)}
                  data-testid="ai-tone-select"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all">
                  {['professional', 'friendly', 'formal', 'casual', 'empathetic'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#a1a1aa] mb-1.5">Confidence Threshold</label>
                <input type="number" min="0" max="1" step="0.05" value={form.ai_config?.confidence_threshold || 0.8}
                  onChange={e => setNested('ai_config', 'confidence_threshold', parseFloat(e.target.value))}
                  data-testid="ai-confidence-input"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs text-[#a1a1aa] mb-1.5">System Instructions</label>
              <textarea value={form.ai_config?.system_instructions || ''} onChange={e => setNested('ai_config', 'system_instructions', e.target.value)}
                rows={3} data-testid="ai-instructions-input"
                className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-[#EF694C] transition-all" />
            </div>
            <div className="flex items-center gap-6 mt-4">
              {[
                { label: 'AI Enabled', key: 'enabled' },
                { label: 'Automatische Antwort', key: 'auto_reply' },
                { label: 'Escalation', key: 'escalation_enabled' },
              ].map(toggle => (
                <label key={toggle.key} className="flex items-center gap-2 cursor-pointer">
                  <div className={`relative w-9 h-5 rounded-full transition-colors ${form.ai_config?.[toggle.key] ? 'bg-[#EF694C]' : 'bg-white/10'}`}
                    onClick={() => setNested('ai_config', toggle.key, !form.ai_config?.[toggle.key])}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.ai_config?.[toggle.key] ? 'translate-x-4' : ''}`} />
                  </div>
                  <span className="text-xs text-[#a1a1aa]">{toggle.label}</span>
                </label>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Right Column */}
        <div className="space-y-5">
          {/* Kanalstatus */}
          <SectionCard title="Kanalstatus" icon={Circle}>
            <div className="space-y-3">
              {Object.entries(tenant.channel_status || {}).map(([ch, status]) => (
                <div key={ch} className="flex items-center justify-between">
                  <span className="text-sm text-[#a1a1aa]">{chLabels[ch] || ch}</span>
                  <span className={`text-xs font-medium flex items-center gap-1.5 ${chStatusColor[status] || 'text-[#52525b]'}`}>
                    <Circle className="w-1.5 h-1.5 fill-current" />{status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Team */}
          <SectionCard title={`Team (${users.length})`} icon={Users}>
            <div className="space-y-2.5">
              {users.slice(0, 5).map(u => (
                <div key={u.id} className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[#EF694C]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#EF694C]">{u.name.charAt(0)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-white truncate">{u.name}</p>
                    <p className="text-xs text-[#52525b]">{u.role}</p>
                  </div>
                  <span className={`ml-auto w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-[#10B981]' : 'bg-[#52525b]'}`} />
                </div>
              ))}
            </div>
          </SectionCard>

          {/* KB Status */}
          <SectionCard title="Wissensdatenbank" icon={BookOpen}>
            <div className="space-y-2">
              {[
                { label: 'Quellen gesamt', value: tenant.kb_status?.total_sources || 0 },
                { label: 'Indexed', value: tenant.kb_status?.indexed_sources || 0 },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between">
                  <span className="text-sm text-[#a1a1aa]">{s.label}</span>
                  <span className="text-sm font-mono text-white">{s.value}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
